import { useEffect, useState } from 'react';

/**
 * `useState` mirror that writes to localStorage whenever the value changes
 * and reads from it on mount. Falls back to `initial` when the key is unset
 * or the value can't be parsed. SSR-safe (no localStorage access on the
 * server side).
 *
 *   const [view, setView] = usePersistedState<'table' | 'cards' | 'list'>(
 *     'admin.tenants.view',
 *     'table'
 *   );
 */
export function usePersistedState<T>(key: string, initial: T): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return initial;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw == null) return initial;
      return JSON.parse(raw) as T;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // localStorage unavailable (private mode, full quota): silent fall-through.
    }
  }, [key, value]);

  return [value, setValue];
}
